import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";

import styles from "./PMSFieldsForm.module.scss";
import classNames from "classnames/bind";
import { useController, useForm } from "react-hook-form";
import { EDataKeys, TData } from "../../types/TData";
import DatePicker from "../DatePicker/DatePicker.com";
import Select from "../SelectNew/Select";
import {
    OCIEnvironmentOptions,
    operaMultiPropertyOptions,
    operaSCOptions,
    operaTypeOptions,
    operaVersionOptions,
    oracleDealerOptions,
    posOptions
} from "../../constants";
import { useUpdateProjectMutation, addMessage } from "../../store/services";
import SlideCheckbox from "../SlideCheckbox";
import { debounce } from "../../helpers";
import Input from "../Input/Input";

const cx = classNames.bind(styles);

function PMSFieldsForm({ project }: Props) {
    const [updateProject] = useUpdateProjectMutation();
    const dispatch = useDispatch();

    // Parse API response and show appropriate message
    const handleUpdateResponse = useCallback((fieldName: string, response: any) => {
        if (response?.data && Array.isArray(response.data)) {
            const result = response.data[0];
            if (result?.status === 200) {
                dispatch(addMessage({ type: 'success', text: `${fieldName} updated successfully` }));
            } else if (result?.status === 400 && result?.errors) {
                const errorMessages = result.errors.map((err: any) => err.message).join(', ');
                dispatch(addMessage({ type: 'error', text: `Failed to update ${fieldName}: ${errorMessages}` }));
            }
        } else if (response?.error) {
            dispatch(addMessage({ type: 'error', text: `Failed to update ${fieldName}: Network error` }));
        }
    }, [dispatch]);

    // Helper function to handle updates with message feedback
    const handleFieldUpdate = (payload: any, fieldName: string) => {
        updateProject(payload)
            .then((response) => {
                handleUpdateResponse(fieldName, response);
            })
            .catch((error) => {
                console.error(`Failed to update ${fieldName}:`, error);
                dispatch(addMessage({ type: 'error', text: `Failed to update ${fieldName}: Network error` }));
            });
    };

    const debouncedUpdateProject = useMemo(() => {
        const debouncedFn = debounce((payload: any, fieldName: string) => {
            updateProject(payload)
                .then((response) => {
                    handleUpdateResponse(fieldName, response);
                })
                .catch((error) => {
                    console.error('Debounced update failed:', error);
                    dispatch(addMessage({ type: 'error', text: `Failed to update ${fieldName}: Network error` }));
                });
        }, 500);
        return debouncedFn;
    }, [updateProject, dispatch, handleUpdateResponse]);

    const { control, reset } = useForm<FormValues>({
        defaultValues: {
            oracleDealer: project[EDataKeys.VENDORS_ORACLE] || "",
            operaVersion: project[EDataKeys.OPERA_VERSION] || "",
            operaType: project[EDataKeys.OPERA_TYPE] || "",
            operaSC: project[EDataKeys.OPERA_SC] || "",
            operaMultiProperty: project[EDataKeys.OPERA_MULTI_PROPERTY] || "",
            operaConfigDate: project[EDataKeys.OPERA_CONFIG_DATE] ? new Date(project[EDataKeys.OPERA_CONFIG_DATE]) : null,
            operaCountDownDate: project[EDataKeys.OPERA_COUNTDOWN_DATE] ? new Date(project[EDataKeys.OPERA_COUNTDOWN_DATE]) : null,
            lockCountDownDate: project[EDataKeys.LOCK_COUNTDOWN_DATE] ?? false,
            ociEnvironment: project[EDataKeys.OCI_ENVIRONMENT] ?? "",
            vendorsPOS: project[EDataKeys.VENDORS_POS] ?? "",
            posVersion: project[EDataKeys.POS_VERSION] ?? "",
        },
        mode: "onChange",
    });

    useEffect(() => {
        reset({
            oracleDealer: project[EDataKeys.VENDORS_ORACLE] || "",
            operaVersion: project[EDataKeys.OPERA_VERSION] || "",
            operaType: project[EDataKeys.OPERA_TYPE] || "",
            operaSC: project[EDataKeys.OPERA_SC] || "",
            operaMultiProperty: project[EDataKeys.OPERA_MULTI_PROPERTY] || "",
            operaConfigDate: project[EDataKeys.OPERA_CONFIG_DATE] ? new Date(project[EDataKeys.OPERA_CONFIG_DATE]) : null,
            operaCountDownDate: project[EDataKeys.OPERA_COUNTDOWN_DATE] ? new Date(project[EDataKeys.OPERA_COUNTDOWN_DATE]) : null,
            lockCountDownDate: project[EDataKeys.LOCK_COUNTDOWN_DATE] ?? false,
            ociEnvironment: project[EDataKeys.OCI_ENVIRONMENT] ?? "",
            vendorsPOS: project[EDataKeys.VENDORS_POS] ?? "",
            posVersion: project[EDataKeys.POS_VERSION] ?? "",
        });
    }, [project, reset])

    const {
        field: oracleDealer, //
    } = useController({
        name: "oracleDealer",
        control,
    });

    const {
        field: operaVersion, //
    } = useController({
        name: "operaVersion",
        control,
    });

    const {
        field: operaType, //
    } = useController({
        name: "operaType",
        control,
    });

    const {
        field: operaSC,
    } = useController({
        name: "operaSC",
        control,
    });

    const {
        field: operaMultiProperty,
    } = useController({
        name: "operaMultiProperty",
        control,
    });

    const {
        field: operaConfigDate,
    } = useController({
        name: "operaConfigDate",
        control,
    });

    const {
        field: operaCountDownDate,
    } = useController({
        name: "operaCountDownDate",
        control,
    });

    const {
        field: lockCountDownDate,
    } = useController({
        name: "lockCountDownDate",
        control,
    });

    const {
        field: ociEnvironment,
    } = useController({
        name: "ociEnvironment",
        control,
    });

    const {
        field: vendorsPOS,
    } = useController({
        name: "vendorsPOS",
        control,
    });

    const {
        field: posVersion,
    } = useController({
        name: "posVersion",
        control,
    });

    return (
        <form className={cx("form")}>
            <div className={cx("row")}>
                <label htmlFor="oracle-dealer">Oracle / Dealer</label>
                <Select
                    placeholder="Select Oracle / Dealer"
                    options={oracleDealerOptions.map((item) => ({
                        value: item,
                        label: item
                    }))}
                    onChange={(value) => {
                        oracleDealer.onChange(value);
                        handleFieldUpdate([{ Id: project.Id, [EDataKeys.VENDORS_ORACLE]: value as string || "" }], "Oracle / Dealer");
                    }}
                    value={oracleDealer.value}
                    isClearable
                    id="oracle-dealer"
                />
            </div>
            <div className={cx("row")}>
                <label htmlFor="opera-version">Opera Version</label>
                <Select
                    placeholder="Select Opera Version"
                    options={operaVersionOptions.map((item) => ({
                        value: item,
                        label: item
                    }))}
                    onChange={(value) => {
                        operaVersion.onChange(value);
                        handleFieldUpdate([{ Id: project.Id, [EDataKeys.OPERA_VERSION]: value as string || "" }], "Opera Version");
                    }}
                    value={operaVersion.value}
                    isClearable
                    id="opera-version"
                />
            </div>
            <div className={cx("row")}>
                <label htmlFor="opera-version">Opera Type</label>
                <Select
                    placeholder="Select Opera Type"
                    options={operaTypeOptions.map((item) => ({
                        value: item,
                        label: item
                    }))}
                    onChange={(value) => {
                        operaType.onChange(value);
                        handleFieldUpdate([{ Id: project.Id, [EDataKeys.OPERA_TYPE]: value as string || "" }], "Opera Type");
                    }}
                    value={operaType.value}
                    isClearable
                    id="opera-version"
                />
            </div>
            <div className={cx("row")}>
                <label htmlFor="oci-environment">OCI Environment</label>
                <Select
                    placeholder="Select OCI Environment"
                    options={OCIEnvironmentOptions.map((item) => ({
                        value: item,
                        label: item
                    }))}
                    onChange={(value) => {
                        ociEnvironment.onChange(value);
                        handleFieldUpdate([{ Id: project.Id, [EDataKeys.OCI_ENVIRONMENT]: value as string || "" }], "OCI Environment");
                    }}
                    value={ociEnvironment.value}
                    isClearable
                    id="oci-environment"
                />
            </div>
            <div className={cx("row")}>
                <label htmlFor="opera-sc">Opera S&C</label>
                <Select
                    placeholder="Select Opera S&C"
                    options={operaSCOptions.map((item) => ({
                        value: item,
                        label: item
                    }))}
                    onChange={(value) => {
                        operaSC.onChange(value);
                        handleFieldUpdate([{ Id: project.Id, [EDataKeys.OPERA_SC]: value as string || "" }], "Opera S&C");
                    }}
                    value={operaSC.value}
                    isClearable
                    id="opera-sc"
                />
            </div>
            <div className={cx("row")}>
                <label htmlFor="opera-multi-property">Opera Multi Property</label>
                <Select
                    placeholder="Select Opera Multi Property"
                    options={operaMultiPropertyOptions.map((item) => ({
                        value: item,
                        label: item
                    }))}
                    onChange={(value) => {
                        operaMultiProperty.onChange(value);
                        handleFieldUpdate([{ Id: project.Id, [EDataKeys.OPERA_MULTI_PROPERTY]: value as string || "" }], "Opera Multi Property");
                    }}
                    value={operaMultiProperty.value}
                    isClearable
                    id="opera-multi-property"
                />
            </div>
            <div className={cx("row")}>
                <label htmlFor="opera-type">Opera Config Date</label>
                <DatePicker
                    onChange={(newDate) => {
                        operaConfigDate.onChange(newDate);
                        handleFieldUpdate([{ Id: project.Id, [EDataKeys.OPERA_CONFIG_DATE]: (newDate as Date)?.toISOString() || null }], "Opera Config Date");
                    }}
                    date={operaConfigDate.value}
                    placeholder="Opera Config Date"
                    id="opera-type"
                />

            </div>
            <div className={cx("row")}>
                <label htmlFor="opera-countdown-date">Opera Countdown Date</label>
                <DatePicker
                    onChange={(newDate) => {
                        operaCountDownDate.onChange(newDate);
                        handleFieldUpdate([{ Id: project.Id, [EDataKeys.OPERA_COUNTDOWN_DATE]: (newDate as Date)?.toISOString() || null }], "Opera Countdown Date");
                    }}
                    date={operaCountDownDate.value}
                    placeholder="Opera Countdown Date"
                    id="opera-countdown-date"
                />
            </div>
            <div className={cx("row", "toggle")}>
                <SlideCheckbox
                    checked={lockCountDownDate.value}
                    onChange={(value) => {
                        lockCountDownDate.onChange(value);
                        handleFieldUpdate([{ Id: project.Id, [EDataKeys.LOCK_COUNTDOWN_DATE]: value }], "Lock CountDown Date");
                    }}
                    renderLabel={() => "Lock CountDown Date"}
                />
            </div>
            <div className={cx("row")}>
                <label htmlFor="pos">POS</label>
                <Select
                    placeholder="POS"
                    options={posOptions.map((item) => ({
                        value: item,
                        label: item
                    }))}
                    onChange={(value) => {
                        vendorsPOS.onChange(value);
                        handleFieldUpdate([{ Id: project.Id, [EDataKeys.VENDORS_POS]: value as string || "" }], "POS");
                    }}
                    value={vendorsPOS.value}
                    isClearable
                    id="pos"
                />
            </div>
            <div className={cx("row")}>
                <label htmlFor="pos-version">POS Version</label>
                
                <Input
                    name='pos-version'
                    onChange={(e) => {
                        posVersion.onChange(e.currentTarget.value);
                        debouncedUpdateProject([{ Id: project.Id, [EDataKeys.POS_VERSION]: e.currentTarget.value || "" }], "POS Version")
                    }}
                    value={posVersion.value}
                    className={cx("pos-version")}
                    placeholder="Type POS Version"
                />
            </div>
        </form>
    );
}

type FormValues = {
    oracleDealer: string,
    operaVersion: string,
    operaType: string,
    operaSC: string,
    operaMultiProperty: string,
    operaConfigDate: Date | null,
    operaCountDownDate: Date | null,
    lockCountDownDate: boolean,
    ociEnvironment: string,
    vendorsPOS: string,
    posVersion: string,
}

type Props = {
    project: TData
}

export default PMSFieldsForm;